import { useSearchParams } from 'react-router-dom';
import { useCampaign } from '../../hooks/use-campaigns';
import { SMSEditor } from '@/components/sms-editor/sms-editor';
import { MessagePreview } from '@/components/message-preview';
import { SectionCard } from '@/components/section-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TestSendInput } from '@/components/test-send-input';
import { serializeForPreview } from '@/components/sms-editor/utils/serializer';
import { toast } from '@/hooks/use-toast';
import { useStep } from '../layout';
import { DesignStepSkeleton } from '@/components/ui/skeletons';
import { PlusCircle } from 'lucide-react';
import { useVariantManagement } from '../../hooks/use-variant-management';
import { SEND_CAMPAIGN_PREVIEW } from '@/mutations/campaigns';
import { useMutation } from '@apollo/client';
import { useMemo } from 'react';

export function DesignPage() {
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('id');
  const { setStepValid } = useStep();
  const { campaign, loading } = useCampaign(campaignId ?? '');

  const [sendPreview] = useMutation<typeof SEND_CAMPAIGN_PREVIEW>(SEND_CAMPAIGN_PREVIEW, {});

  const {
    activeVariantId,
    variantName,
    editorState,
    handleVariantSelect,
    handleVariantNameChange,
    handleEditorChange
  } = useVariantManagement({
    campaignId,
    campaign,
    onValidityChange: setStepValid
  });

  const sortedVariants = useMemo(
    () => [...(campaign?.variants ?? [])].sort((a, b) => a.variantId.localeCompare(b.variantId)),
    [campaign?.variants]
  );

  const handleSendTest = (phoneNumber: string) => {
    void (async () => {
      try {
        await sendPreview({
          variables: {
            input: {
              phoneNumber,
              customElements: editorState
            }
          }
        });

        toast({
          title: 'Test message sent',
          description: `Message sent to ${phoneNumber}`
        });
      } catch (error) {
        console.error('Failed to send test message:', error);
        toast({
          title: 'Failed to send test message',
          description: 'An error occurred while sending the test message.',
          variant: 'destructive'
        });
      }
    })();
  };

  if (loading || !campaign) {
    return <DesignStepSkeleton />;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-8">
        {/* Variant Section */}
        <div className="flex flex-col gap-2">
          <div className="text-sm font-medium">Variant</div>
          <div className="flex items-center gap-3">
            {/* Main Variant Buttons */}
            <div className="flex gap-2">
              {sortedVariants.map((variant) => (
                <Button
                  key={variant.variantId}
                  variant={variant.variantId === activeVariantId ? 'default' : 'outline'}
                  className="border rounded-lg"
                  onClick={() => {
                    handleVariantSelect(variant.variantId, variant.variantName);
                  }}
                >
                  {variant.variantId === activeVariantId ? variantName : variant.variantName}{' '}
                  <span className="ml-1 text-xs text-muted-foreground font-mono">
                    {variant.splitWeight}%
                  </span>
                </Button>
              ))}
            </div>

            {/* Follow-up Buttons */}
            <div className="flex gap-2">
              <Button variant="outline" className="gap-2">
                <PlusCircle className="h-4 w-4" />
                {`Didn't click follow up`}
              </Button>
              <Button variant="outline" className="gap-2">
                <PlusCircle className="h-4 w-4" />
                {`Clicked, didn't purchase follow up`}
              </Button>
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          {/* Left column - editor (60%) */}
          <div className="w-[60%] flex flex-col gap-4">
            <SectionCard title="Messaging">
              <div className="space-y-6">
                <div>
                  <Label>Variant Name</Label>
                  <Input
                    value={variantName}
                    onChange={handleVariantNameChange}
                    placeholder="e.g. Control, Test A, Test B"
                  />
                  <div className="mt-1 text-sm text-muted-foreground">
                    Your recipients will not see the variant name
                  </div>
                </div>

                <div>
                  <Label>Message Content</Label>
                  <div className="mt-2 min-h-[300px] rounded-md bg-card">
                    <SMSEditor initialValue={editorState} onChange={handleEditorChange} />
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>

          {/* Right column - preview (40%) */}
          <div className="w-[40%]">
            <SectionCard title="Preview">
              <div className="space-y-6 flex flex-col items-center">
                {/* Phone Preview */}
                <div className="flex-1 flex items-center justify-center py-8">
                  <MessagePreview content={serializeForPreview(editorState)} />
                </div>

                {/* Test Send Section */}
                <div className="space-y-2">
                  <Label>Send Test Message</Label>
                  <TestSendInput onSendTest={handleSendTest} />
                </div>
              </div>
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}
